import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, Divider, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import RegistroCapacitacionAPI, { Pregunta, ResultadoEvaluacion } from "src/services/registroCapacitacion";

interface PendienteItem {
  Id: number;
  TemaId: number;
  FechaCapa: string;
  Descripcion: string;
}

interface PreguntaResultado {
  pregunta: string;
  opcionSeleccionada: string;
  opcionCorrecta: string;
  esCorrecta: boolean;
}

interface ResultadoTema {
  tema: string;
  preguntas: PreguntaResultado[];
  correctas: number;
  total: number;
  puntaje: number;
}

interface Props {
  isAuth: boolean;
  onPendientesChange?: (count: number) => void;
  // Si es true, oculta el botón de saltar/cerrar mientras hay evaluaciones pendientes.
  // Se usa cuando el usuario entra a una ruta bloqueada (salarios, historial, reclamos).
  bloqueado?: boolean;
  // Contador que el padre incrementa para forzar la reapertura del modal
  // (por ej. al ingresar a una ruta bloqueada con evaluaciones pendientes).
  solicitudReapertura?: number;
}

type Vista = "evaluacion" | "resumen" | "cerrado";

const colorPuntaje = (p: number) => (p >= 60 ? "#2e7d32" : "#c62828");

const EvaluacionPendienteModal: React.FC<Props> = ({
  isAuth,
  onPendientesChange,
  bloqueado = false,
  solicitudReapertura = 0,
}) => {
  const [present] = useIonToast();
  const history = useHistory();

  const [vista, setVista] = useState<Vista>("cerrado");
  const [cola, setCola] = useState<PendienteItem[]>([]);
  const [actual, setActual] = useState<PendienteItem | null>(null);
  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  const [respuestas, setRespuestas] = useState<Record<number, number>>({});
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoTema[]>([]);

  const yaVerificado = useRef(false);
  const colaRef = useRef<PendienteItem[]>([]);
  const resultadosRef = useRef<ResultadoTema[]>([]);
  // Refleja la cantidad de evaluaciones realmente pendientes en el backend.
  // Solo decrece cuando se guarda una evaluación con éxito; saltar NO la modifica.
  const pendientesEnDbRef = useRef<number>(0);

  const setPendientesEnDb = (n: number) => {
    pendientesEnDbRef.current = n;
    onPendientesChange?.(n);
  };

  useEffect(() => { colaRef.current = cola; }, [cola]);
  useEffect(() => { resultadosRef.current = resultados; }, [resultados]);

  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setCola([]);
      setActual(null);
      setResultados([]);
      setVista("cerrado");
      setPendientesEnDb(0);
      return;
    }
    if (yaVerificado.current) return;
    yaVerificado.current = true;

    const verificar = async () => {
      try {
        const res = await RegistroCapacitacionAPI.listarPendientes();
        const pendientes: PendienteItem[] = res.data?.datos || [];
        setPendientesEnDb(pendientes.length);
        if (pendientes.length === 0) return;
        colaRef.current = pendientes.slice(1);
        setCola(pendientes.slice(1));
        cargarPreguntas(pendientes[0]);
      } catch {
        // silencioso
      }
    };
    verificar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // Reapertura forzada (cuando el padre solicita que el modal se vuelva a abrir,
  // ej. al entrar a una ruta bloqueada). Re-fetch para tener el estado actualizado.
  useEffect(() => {
    if (!isAuth || solicitudReapertura === 0) return;
    if (vista === "evaluacion") return; // ya está abierto

    const reabrir = async () => {
      try {
        const res = await RegistroCapacitacionAPI.listarPendientes();
        const pendientes: PendienteItem[] = res.data?.datos || [];
        setPendientesEnDb(pendientes.length);
        if (pendientes.length === 0) return;
        setResultados([]);
        resultadosRef.current = [];
        colaRef.current = pendientes.slice(1);
        setCola(pendientes.slice(1));
        cargarPreguntas(pendientes[0]);
      } catch {
        // silencioso
      }
    };
    reabrir();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudReapertura]);

  const cargarPreguntas = async (item: PendienteItem) => {
    setActual(item);
    setRespuestas({});
    setVista("evaluacion");
    setCargando(true);
    try {
      const res = await RegistroCapacitacionAPI.listarPreguntas(item.TemaId);
      setPreguntas(res.data?.datos || []);
    } catch {
      setPreguntas([]);
    } finally {
      setCargando(false);
    }
  };

  const avanzarOResumen = (nuevoResultado: ResultadoTema) => {
    const acumulados = [...resultadosRef.current, nuevoResultado];
    resultadosRef.current = acumulados;
    setResultados(acumulados);

    const pendientes = colaRef.current;
    if (pendientes.length > 0) {
      const [siguiente, ...resto] = pendientes;
      colaRef.current = resto;
      setCola(resto);
      cargarPreguntas(siguiente);
    } else {
      setVista("resumen");
    }
  };

  const handleOmitir = () => {
    const pendientes = colaRef.current;
    if (pendientes.length > 0) {
      const [siguiente, ...resto] = pendientes;
      colaRef.current = resto;
      setCola(resto);
      cargarPreguntas(siguiente);
    } else {
      // Saltó la última: mostrar resumen si completó alguna, sino cerrar
      if (resultadosRef.current.length > 0) {
        setVista("resumen");
      } else {
        setVista("cerrado");
      }
    }
  };

  // Cierra el modal completo (todas las pendientes quedan sin completar).
  // Si está en modo bloqueado (entró a salarios/historial/reclamos), redirige
  // al home así no puede acceder al módulo protegido.
  const handleSaltarTodo = () => {
    colaRef.current = [];
    setCola([]);
    setActual(null);
    setVista("cerrado");
    if (bloqueado) {
      history.replace("/home");
    }
  };

  const handleEnviar = async () => {
    if (!actual) return;

    const sinResponder = preguntas.filter((p) => respuestas[p.Id] === undefined);
    if (sinResponder.length > 0) {
      present({ message: "Debe responder todas las preguntas", duration: 3000, color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      // La corrección la hace el backend: el cliente no conoce la opción correcta.
      const res = await RegistroCapacitacionAPI.guardarEvaluacion({
        CapacitacionId: actual.Id,
        Respuestas: preguntas.map((p) => ({ PreguntaId: p.Id, OpcionId: respuestas[p.Id] })),
      });
      const r: ResultadoEvaluacion = res.data?.resultado;

      // Decrementar el conteo "real" (esta evaluación ya fue guardada)
      setPendientesEnDb(Math.max(0, pendientesEnDbRef.current - 1));

      const resultado: ResultadoTema = {
        tema: actual.Descripcion || "—",
        correctas: r?.Correctas ?? 0,
        total: r?.Total ?? preguntas.length,
        puntaje: Math.round(r?.Puntaje ?? 0),
        preguntas: (r?.Detalle || []).map((d) => ({
          pregunta: d.Pregunta,
          opcionSeleccionada: d.Respuesta || "—",
          opcionCorrecta: d.Correcta || "—",
          esCorrecta: d.EsCorrecta,
        })),
      };

      avanzarOResumen(resultado);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar la evaluación",
        duration: 4000,
        color: "danger",
      });
      // 409: ya estaba evaluada (doble envío) -> pasar a la siguiente;
      // si no, las preguntas cambiaron mientras respondía -> recargarlas.
      if (err?.response?.status === 409) {
        if (String(err?.response?.data?.error || "").includes("Ya completaste")) {
          setPendientesEnDb(Math.max(0, pendientesEnDbRef.current - 1));
          handleOmitir();
        } else {
          cargarPreguntas(actual);
        }
      }
    } finally {
      setGuardando(false);
    }
  };

  const totalPendientes = cola.length + (actual && vista === "evaluacion" ? 1 : 0);

  // ── Contenido: resumen ───────────────────────────────────────────────────
  const renderResumen = () => {
    const puntajeGlobal =
      resultados.length > 0
        ? Math.round(resultados.reduce((s, r) => s + r.puntaje, 0) / resultados.length)
        : 0;

    return (
      <>
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontSize: 16 }}>Resumen de evaluaciones</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: 16 }}>
            <div
              style={{
                textAlign: "center",
                background: colorPuntaje(puntajeGlobal),
                color: "#fff",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 24,
              }}
            >
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {puntajeGlobal}%
              </Typography>
              <Typography variant="body2">Puntaje promedio global</Typography>
            </div>

            {resultados.map((r, ri) => (
              <div key={ri} style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, flex: 1 }}>
                    {r.tema}
                  </Typography>
                  <span
                    style={{
                      background: colorPuntaje(r.puntaje),
                      color: "#fff",
                      borderRadius: 12,
                      padding: "2px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      marginLeft: 8,
                    }}
                  >
                    {r.puntaje}% ({r.correctas}/{r.total})
                  </span>
                </div>

                {r.preguntas.map((p, pi) => (
                  <div
                    key={pi}
                    style={{
                      background: p.esCorrecta ? "#e8f5e9" : "#ffebee",
                      borderLeft: `4px solid ${colorPuntaje(p.esCorrecta ? 100 : 0)}`,
                      borderRadius: 4,
                      padding: "8px 12px",
                      marginBottom: 8,
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                      {pi + 1}. {p.pregunta}
                    </Typography>
                    <Typography variant="body2" style={{ color: colorPuntaje(p.esCorrecta ? 100 : 0) }}>
                      Tu respuesta: {p.opcionSeleccionada}
                    </Typography>
                    {!p.esCorrecta && (
                      <Typography variant="body2" style={{ color: "#2e7d32" }}>
                        Correcta: {p.opcionCorrecta}
                      </Typography>
                    )}
                  </div>
                ))}

                {ri < resultados.length - 1 && <Divider style={{ marginTop: 16 }} />}
              </div>
            ))}

            <IonButton expand="block" style={{ marginTop: 8 }} onClick={() => setVista("cerrado")}>
              Cerrar
            </IonButton>
          </div>
        </IonContent>
      </>
    );
  };

  // ── Contenido: evaluación ────────────────────────────────────────────────
  const renderEvaluacion = () => (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ fontSize: 16 }}>
            Evaluación pendiente
            {totalPendientes > 1 ? ` — quedan ${totalPendientes}` : ""}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSaltarTodo} disabled={guardando}>
              Saltar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {actual && (
          <div style={{ padding: "12px 16px 4px" }}>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
              Tema: <strong>{actual.Descripcion || "—"}</strong>
            </Typography>
            {bloqueado && (
              <Typography variant="caption" style={{ color: "#c62828", display: "block" }}>
                Para acceder a esta sección debés completar las evaluaciones pendientes. Si saltás, volverás al inicio.
              </Typography>
            )}
          </div>
        )}

        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : preguntas.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Typography color="textSecondary">No hay preguntas para este tema.</Typography>
            <IonButton expand="block" style={{ marginTop: 16 }} onClick={handleOmitir}>
              {cola.length > 0 ? "Siguiente" : "Cerrar"}
            </IonButton>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Responda todas las preguntas para completar la evaluación.
            </Typography>

            {preguntas.map((p, idx) => (
              <div key={p.Id} style={{ marginBottom: 24 }}>
                <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                  {idx + 1}. {p.Pregunta}
                </Typography>
                <IonList style={{ margin: "0 -16px" }}>
                  <IonRadioGroup
                    value={respuestas[p.Id] ?? null}
                    onIonChange={(e) =>
                      setRespuestas((prev) => ({ ...prev, [p.Id]: Number(e.detail.value) }))
                    }
                  >
                    {p.Opciones.map((o) => (
                      <IonItem key={o.Id}>
                        <IonLabel style={{ whiteSpace: "normal" }}>{o.Opcion}</IonLabel>
                        <IonRadio slot="start" value={o.Id} />
                      </IonItem>
                    ))}
                  </IonRadioGroup>
                </IonList>
              </div>
            ))}

            <IonButton
              expand="block"
              onClick={handleEnviar}
              disabled={guardando}
              style={{ marginTop: 8 }}
            >
              {guardando ? "Guardando..." : cola.length > 0 ? "Enviar y continuar" : "Enviar evaluación"}
            </IonButton>
          </div>
        )}
      </IonContent>
    </>
  );

  return (
    <IonModal isOpen={vista !== "cerrado"} backdropDismiss={false}>
      {vista === "resumen" ? renderResumen() : renderEvaluacion()}
    </IonModal>
  );
};

export default EvaluacionPendienteModal;
